import { z } from 'zod';
import { baseEntityFields, idSchema, isoDateTimeSchema, propertyMapSchema, customPropertyMapSchema, } from './common.schema';
import { dispatchLinkageSchema } from './client.schema';
/**
 * Resource — a monitored thing: device, website, or cloud object.
 * Properties drive sensor targeting (property-expression AppliesTo
 * equivalent) and auto-grouping (PRD §2.2).
 */
export const RESOURCE_KINDS = ['device', 'website', 'cloud'];
export const WEBSITE_CHECK_METHODS = ['GET', 'HEAD', 'POST'];
/** Configuration specific to website/synthetic checks (LM Uptime class). */
export const websiteConfigSchema = z
    .object({
    url: z.string().url(),
    method: z.enum(WEBSITE_CHECK_METHODS).default('GET'),
    intervalSeconds: z.number().int().min(30).max(3600).default(300),
    timeoutSeconds: z.number().int().min(1).max(120).default(30),
    /** Substring that must appear in the response body for success. */
    expectedContent: z.string().max(1024).optional(),
    expectedStatusCodes: z
        .array(z.number().int().min(100).max(599))
        .min(1)
        .default([200]),
    /** Check TLS certificate validity/expiry (PRD §2.3 — P0). */
    checkSsl: z.boolean().default(true),
    /** Warn this many days before certificate expiry. */
    sslExpiryWarningDays: z.number().int().min(1).max(365).default(30),
    /** Run from cloud probes, on-site collectors, or both. */
    checkFrom: z.enum(['cloud', 'collector', 'both']).default('cloud'),
    /** Cloud probe regions (#5 multi-region): the site counts as DOWN
     *  only when ALL configured regions fail; one failing region is a
     *  degradation signal. 1-2 regions at MVP (us, eu). */
    regions: z.array(z.enum(['us', 'eu'])).min(1).max(2).default(['us']),
})
    .strict();
export const resourceInputSchema = z
    .object({
    name: z.string().min(1).max(253),
    displayName: z.string().min(1).max(200).optional(),
    kind: z.enum(RESOURCE_KINDS),
    clientId: idSchema.optional(),
    collectorId: idSchema.optional(),
    /** IP address or hostname the collector reaches the device at. */
    address: z.string().min(1).max(253).optional(),
    description: z.string().max(2000).optional(),
    /** Custom properties: custom.* ONLY — system/auto namespaces are
     *  server-managed; accepting them here would allow provenance spoofing. */
    properties: customPropertyMapSchema.optional(),
    /** Static group memberships (dynamic groups are property-driven). */
    groups: z.array(z.string().min(1).max(200)).max(100).optional(),
    website: websiteConfigSchema.optional(),
    dispatch: dispatchLinkageSchema.optional(),
    monitoringEnabled: z.boolean().default(true),
})
    .strict()
    .superRefine((val, ctx) => resourceKindRules(val, ctx));
/**
 * Kind-specific field rules, applied on create and re-applied server-side
 * against the merged document on update. Both required AND forbidden
 * fields are enforced so inconsistent documents can't be stored
 * (fail-closed; don't push correctness downstream).
 */
export function resourceKindRules(val, ctx) {
    if (val.kind === 'website') {
        if (!val.website) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['website'],
                message: 'website config is required when kind is "website"',
            });
        }
        if (val.address) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['address'],
                message: 'address is not allowed for website resources (URL lives in website config)',
            });
        }
        const cfg = val.website;
        // checkFrom defaults to 'cloud' when omitted (websiteConfigSchema).
        const checkFrom = cfg?.checkFrom ?? 'cloud';
        if (checkFrom !== 'cloud' && !val.collectorId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['collectorId'],
                message: 'collectorId is required when website checks run from a collector',
            });
        }
        if (checkFrom === 'cloud' && val.collectorId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['collectorId'],
                message: 'collectorId is not allowed for cloud-only website checks (set checkFrom to "collector" or "both")',
            });
        }
    }
    else {
        if (val.website) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['website'],
                message: `website config is not allowed when kind is "${val.kind}"`,
            });
        }
    }
    if (val.kind === 'device') {
        if (!val.collectorId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['collectorId'],
                message: 'collectorId is required when kind is "device"',
            });
        }
        if (!val.address) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['address'],
                message: 'address is required when kind is "device"',
            });
        }
    }
    if (val.kind === 'cloud') {
        if (val.collectorId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['collectorId'],
                message: 'cloud resources are polled from the control plane, not a collector',
            });
        }
        if (val.address) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['address'],
                message: 'address is not allowed for cloud resources',
            });
        }
    }
}
/** Update accepts any subset; cross-field rules re-checked server-side
 *  against the merged document. */
export const resourceUpdateSchema = z
    .object({
    name: z.string().min(1).max(253).optional(),
    displayName: z.string().min(1).max(200).nullable().optional(),
    clientId: idSchema.nullable().optional(),
    collectorId: idSchema.nullable().optional(),
    address: z.string().min(1).max(253).nullable().optional(),
    description: z.string().max(2000).nullable().optional(),
    properties: customPropertyMapSchema.optional(),
    groups: z.array(z.string().min(1).max(200)).max(100).optional(),
    /** Nullable so a website config can be cleared (kind rules are
     *  re-checked server-side against the merged document). */
    website: websiteConfigSchema.nullable().optional(),
    dispatch: dispatchLinkageSchema.nullable().optional(),
    monitoringEnabled: z.boolean().optional(),
})
    .strict();
export const resourceSchema = z
    .object({
    name: z.string().min(1).max(253),
    displayName: z.string().min(1).max(200).nullable(),
    kind: z.enum(RESOURCE_KINDS),
    clientId: idSchema.nullable(),
    collectorId: idSchema.nullable(),
    address: z.string().min(1).max(253).nullable(),
    description: z.string().max(2000).nullable(),
    /** Merged property map: system.* (server/discovery-managed) + custom.*. */
    properties: propertyMapSchema,
    groups: z.array(z.string().min(1).max(200)),
    website: websiteConfigSchema.nullable(),
    dispatch: dispatchLinkageSchema.nullable(),
    monitoringEnabled: z.boolean(),
    /** Set by discovery when the resource was auto-added by a scan. */
    discoveredAt: isoDateTimeSchema.nullable(),
    /** Down/no-data state summary maintained by the evaluator. */
    lastDataAt: isoDateTimeSchema.nullable(),
})
    .extend(baseEntityFields)
    .strict()
    // Stored docs enforce the same kind invariants as inputs — an invalid
    // persisted resource must fail parsing, not flow downstream (fail-closed).
    .superRefine((val, ctx) => resourceKindRules(val, ctx));
/**
 * Instance — a discovered sub-component of a resource (interface, disk,
 * volume, VM, service) with lifecycle (LM Active Discovery equivalent).
 */
export const INSTANCE_STATUSES = ['active', 'retired'];
export const instanceSchema = z
    .object({
    resourceId: idSchema,
    sensorId: idSchema,
    /** Unique key from discovery (e.g. interface ifIndex, disk letter). */
    wildValue: z.string().min(1).max(512),
    name: z.string().min(1).max(253),
    displayName: z.string().min(1).max(200).nullable(),
    properties: propertyMapSchema,
    status: z.enum(INSTANCE_STATUSES),
    autoDiscovered: z.boolean(),
    firstSeenAt: isoDateTimeSchema,
    lastSeenAt: isoDateTimeSchema,
    retiredAt: isoDateTimeSchema.nullable(),
})
    .extend(baseEntityFields)
    .strict()
    .superRefine((val, ctx) => {
    // Lifecycle pairing: retired ⇔ retiredAt set.
    if (val.status === 'retired' && val.retiredAt === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['retiredAt'],
            message: 'retired instances require retiredAt',
        });
    }
    if (val.status === 'active' && val.retiredAt !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['retiredAt'],
            message: 'active instances must not carry retiredAt',
        });
    }
});
/** Manual instance creation/edit (rare; discovery owns most instances). */
export const instanceInputSchema = z
    .object({
    resourceId: idSchema,
    sensorId: idSchema,
    wildValue: z.string().min(1).max(512),
    name: z.string().min(1).max(253),
    displayName: z.string().min(1).max(200).optional(),
    /** Manual instances accept custom.* properties only. */
    properties: customPropertyMapSchema.optional(),
})
    .strict();
//# sourceMappingURL=resource.schema.js.map